import React from 'react';
import { useCountUp } from '../../hooks/useCountUp';

// Common visual primitives reused across admin pages — keeps each page
// focused on data + behavior instead of restyling cards every time.

export const Card = ({ children, className = '', padding = 'p-5' }) => (
  <div className={`bg-[#0a0a1e] border border-[rgba(108,71,255,.15)] rounded-xl ${padding} ${className}`}>
    {children}
  </div>
);

export const SectionHeader = ({ icon: Icon, title, action }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4 text-violet-300" />}
      <h3 className="text-sm font-semibold text-white">{title}</h3>
    </div>
    {action}
  </div>
);

export const StatTile = ({ icon: Icon, label, value, accent = 'violet', sublabel, formatter }) => {
  const accents = {
    violet: 'bg-violet-900/30 text-violet-300',
    blue:   'bg-blue-900/30 text-blue-300',
    emerald:'bg-emerald-900/30 text-emerald-300',
    amber:  'bg-amber-900/30 text-amber-300',
    red:    'bg-red-900/30 text-red-300',
    gray:   'bg-gray-800/40 text-gray-300',
  };
  // Count-up numeric values; pass strings through untouched. If a formatter
  // is provided, it's applied to the animated number (used for currency).
  const animated = useCountUp(typeof value === 'number' ? value : 0);
  const display = typeof value === 'number'
    ? (formatter ? formatter(animated) : animated)
    : value;
  return (
    <div className="bg-[#0a0a1e] border border-[rgba(108,71,255,.15)] rounded-xl p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-[rgba(108,71,255,.3)]">
      <div className="flex items-center gap-3 mb-2">
        {Icon && (
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${accents[accent]}`}>
            <Icon className="h-5 w-5" />
          </div>
        )}
        <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-2xl font-bold text-white tabular-nums">{display}</p>
      {sublabel && <p className="text-[11px] text-gray-500 mt-1">{sublabel}</p>}
    </div>
  );
};

export const Badge = ({ tone = 'gray', children, className = '' }) => {
  const tones = {
    violet:  'text-violet-300 bg-violet-900/40 border-violet-600/40',
    blue:    'text-blue-300 bg-blue-900/40 border-blue-600/40',
    emerald: 'text-emerald-300 bg-emerald-900/40 border-emerald-600/40',
    amber:   'text-amber-300 bg-amber-900/40 border-amber-600/40',
    red:     'text-red-300 bg-red-900/40 border-red-600/40',
    gray:    'text-gray-300 bg-gray-800/50 border-gray-700/40',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${tones[tone]} ${className}`}>
      {children}
    </span>
  );
};

export const StatusDot = ({ status }) => {
  const cfg = {
    active:    { color: 'bg-emerald-400', label: 'Actif',     text: 'text-emerald-300' },
    trialing:  { color: 'bg-blue-400',    label: 'Essai',     text: 'text-blue-300' },
    past_due:  { color: 'bg-amber-400',   label: 'Impayé',    text: 'text-amber-300' },
    canceled:  { color: 'bg-gray-500',    label: 'Annulé',    text: 'text-gray-400' },
    suspended: { color: 'bg-amber-400',   label: 'Suspendu',  text: 'text-amber-300' },
    cancelled: { color: 'bg-red-400',     label: 'Annulé',    text: 'text-red-300' },
    invited:   { color: 'bg-blue-400',    label: 'Invité',    text: 'text-blue-300' },
    disabled:  { color: 'bg-gray-500',    label: 'Désactivé', text: 'text-gray-400' },
  }[status] || { color: 'bg-gray-500', label: status || '—', text: 'text-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.color}`} />
      {cfg.label}
    </span>
  );
};

export const PlanBadge = ({ code, name }) => {
  const tone = { starter: 'gray', pro: 'blue', team: 'emerald', enterprise: 'violet' }[code] || 'gray';
  return <Badge tone={tone}>{name || code || 'Gratuit'}</Badge>;
};

export const formatDate = (s) => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const formatDateTime = (s) => {
  if (!s) return '—';
  return new Date(s).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const formatEuros = (n) => {
  if (n == null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
};

export const Spinner = ({ className = 'h-8 w-8' }) => (
  <div className={`animate-spin rounded-full border-b-2 border-violet-500 ${className}`} />
);

export const Modal = ({ open, onClose, title, children, danger = false, maxWidth = 'max-w-md' }) => {
  if (!open) return null;
  return (
    <div className="sd-modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className={`sd-modal-panel bg-[#0d0d24] border rounded-2xl w-full ${maxWidth} shadow-2xl ${danger ? 'border-red-700/40' : 'border-[rgba(108,71,255,.25)]'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(108,71,255,.12)]">
            <h2 className="text-sm font-semibold text-white">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors text-lg leading-none">×</button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export const TextField = ({ label, hint, ...rest }) => (
  <div>
    {label && <label className="block text-xs font-medium text-gray-400 mb-1.5">{label}{hint && <span className="text-gray-600 ml-1">{hint}</span>}</label>}
    <input
      {...rest}
      className={`w-full px-3 py-2 bg-[#05050f] border border-[rgba(108,71,255,.2)] rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-violet-500/60 ${rest.className || ''}`}
    />
  </div>
);

export const SelectField = ({ label, children, ...rest }) => (
  <div>
    {label && <label className="block text-xs font-medium text-gray-400 mb-1.5">{label}</label>}
    <select
      {...rest}
      className={`w-full px-3 py-2 bg-[#05050f] border border-[rgba(108,71,255,.2)] rounded-lg text-sm text-white focus:outline-none focus:border-violet-500/60 ${rest.className || ''}`}
    >
      {children}
    </select>
  </div>
);

export const Btn = ({ tone = 'primary', children, className = '', ...rest }) => {
  const tones = {
    primary: 'bg-violet-600 hover:bg-violet-500 text-white border-transparent',
    ghost:   'bg-transparent text-gray-300 hover:text-white border-[rgba(108,71,255,.25)] hover:border-violet-500/60',
    danger:  'bg-red-600/90 hover:bg-red-500 text-white border-transparent',
    subtle:  'bg-[rgba(108,71,255,.1)] text-gray-200 hover:bg-[rgba(108,71,255,.2)] border-[rgba(108,71,255,.2)]',
  };
  return (
    <button
      {...rest}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${tones[tone]} ${className}`}
    >
      {children}
    </button>
  );
};
